<template>
  <div class="admin-tab">
    <n-space vertical :size="24" style="padding: 24px;">
      <!-- User Management Section -->
      <n-card title="User Management">
        <template #header-extra>
          <n-button type="primary" @click="showCreateUserModal = true">
            Create User
          </n-button>
        </template>

        <n-data-table
          :columns="userColumns"
          :data="users"
          :loading="loadingUsers"
          :pagination="false"
          :bordered="true"
        />
      </n-card>

      <!-- External API connectivity -->
      <n-card title="API connectivity">
        <template #header-extra>
          <n-button type="primary" secondary :loading="loadingIntegrationHealth" @click="runIntegrationHealth">
            Check APIs
          </n-button>
        </template>
        <p class="api-hint">
          Verifies Apify (<code>/v2/users/me</code>), DigitalOcean Spaces, Ollama, Python worker, and optional AI keys.
        </p>
        <n-alert v-if="integrationCheckedAt" type="info" style="margin-bottom: 12px;" :title="`Last run: ${integrationCheckedAt}`" />
        <n-data-table
          v-if="integrationChecks.length"
          :columns="integrationColumns"
          :data="integrationChecks"
          :pagination="false"
          size="small"
          :bordered="true"
        />
        <n-empty v-else description="Click “Check APIs” to test integrations" />
      </n-card>

      <!-- Brand Management Section -->
      <n-card title="Brand Management">
        <n-data-table
          :columns="brandColumns"
          :data="brands"
          :loading="loadingBrands"
          :pagination="false"
          :bordered="true"
        />
      </n-card>

      <!-- Database Browser Section -->
      <n-card title="Database Statistics">
        <n-grid :cols="3" :x-gap="16" :y-gap="16">
          <n-gi v-for="(count, tableName) in dbStats" :key="tableName">
            <n-statistic :label="tableName" :value="count" />
          </n-gi>
        </n-grid>
      </n-card>

      <!-- RefImages Table Section -->
      <n-card title="RefImages Debug Table">
        <template #header-extra>
          <n-space>
            <n-input
              v-model:value="refImagesSearch"
              placeholder="Search..."
              clearable
              style="width: 200px;"
              @keyup.enter="handleRefImagesSearch"
            />
            <n-select
              v-model:value="refImagesSortBy"
              :options="refImagesSortOptions"
              style="width: 180px;"
              @update:value="loadRefImages"
            />
            <n-button-group>
              <n-button
                :type="refImagesSortOrder === 'asc' ? 'primary' : 'default'"
                @click="refImagesSortOrder = 'asc'; loadRefImages()"
              >
                Asc
              </n-button>
              <n-button
                :type="refImagesSortOrder === 'desc' ? 'primary' : 'default'"
                @click="refImagesSortOrder = 'desc'; loadRefImages()"
              >
                Desc
              </n-button>
            </n-button-group>
          </n-space>
        </template>

        <n-data-table
          :columns="refImagesColumns"
          :data="refImages"
          :loading="loadingRefImages"
          :bordered="true"
          :pagination="refImagesPagination"
          @update:page="handleRefImagesPageChange"
          @update:page-size="handleRefImagesPageSizeChange"
          :scroll-x="1200"
        />
      </n-card>
    </n-space>

    <!-- Create User Modal -->
    <n-modal
      v-model:show="showCreateUserModal"
      preset="dialog"
      title="Create New User"
      positive-text="Create"
      negative-text="Cancel"
      @positive-click="handleCreateUser"
      @negative-click="showCreateUserModal = false"
    >
      <n-form
        ref="createUserFormRef"
        :model="createUserForm"
        :rules="createUserRules"
        label-placement="left"
        label-width="100"
      >
        <n-form-item label="Email" path="email">
          <n-input v-model:value="createUserForm.email" placeholder="user@example.com" />
        </n-form-item>
        <n-form-item label="Name" path="name">
          <n-input v-model:value="createUserForm.name" placeholder="Optional" />
        </n-form-item>
        <n-form-item label="Role" path="role">
          <n-select
            v-model:value="createUserForm.role"
            :options="roleOptions"
            placeholder="Select role"
          />
        </n-form-item>
        <n-alert type="info" style="margin-top: 16px;">
          Default password: <strong>inc-agency-2026</strong>
        </n-alert>
      </n-form>
    </n-modal>

    <!-- Delete Brand Confirmation Dialog -->
    <n-modal
      v-model:show="showDeleteDialog"
      preset="dialog"
      title="Delete Brand"
      positive-text="Delete"
      negative-text="Cancel"
      type="warning"
      @positive-click="confirmDeleteBrand"
      @negative-click="cancelDeleteBrand"
    >
      <div v-if="brandToDelete">
        <p>Are you sure you want to delete "<strong>{{ brandToDelete.name }}</strong>" ({{ brandToDelete.code }})?</p>
        <p style="margin-top: 12px;">This will permanently delete:</p>
        <ul style="margin-top: 8px; padding-left: 20px;">
          <li>All projects associated with this brand</li>
          <li>All comps, assets, and deliverables</li>
          <li>All images associated with this brand</li>
          <li>All layouts for this brand</li>
        </ul>
        <p style="margin-top: 12px; color: #ff4d4f; font-weight: 500;">This action cannot be undone.</p>
      </div>
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, h } from 'vue'
import { useMessage } from 'naive-ui'
import { apiClient, type User } from '@/services/api'
import type { FormInst, DataTableColumns } from 'naive-ui'

const message = useMessage()

// User management state
const users = ref<User[]>([])
const loadingUsers = ref(false)
const showCreateUserModal = ref(false)
const createUserFormRef = ref<FormInst | null>(null)
const createUserForm = ref({
  email: '',
  name: '',
  role: 'user',
})

const roleOptions = [
  { label: 'User', value: 'user' },
  { label: 'Moderator', value: 'moderator' },
  { label: 'Admin', value: 'admin' },
]

const createUserRules = {
  email: [
    { required: true, message: 'Email is required', trigger: 'blur' },
    { type: 'email', message: 'Invalid email format', trigger: 'blur' },
  ],
}

// Brand management state
const brands = ref<Array<{
  id: string;
  name: string;
  code: string;
  brandCode?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}>>([])
const loadingBrands = ref(false)
const showDeleteDialog = ref(false)
const brandToDelete = ref<any>(null)

// Database stats state
const dbStats = ref<Record<string, number>>({})
const loadingDbStats = ref(false)

// Integration health (admin API connectivity)
interface IntegrationCheckRow {
  id: string
  label: string
  ok: boolean
  latencyMs: number
  detail?: string
}
const integrationChecks = ref<IntegrationCheckRow[]>([])
const integrationCheckedAt = ref('')
const loadingIntegrationHealth = ref(false)

const integrationColumns: DataTableColumns<IntegrationCheckRow> = [
  {
    title: 'Service',
    key: 'label',
    width: 220,
    ellipsis: { tooltip: true },
  },
  {
    title: 'Status',
    key: 'ok',
    width: 100,
    render: (row) => {
      const skipped = row.detail?.toLowerCase().includes('skipped')
      if (skipped) {
        return h('span', { style: { color: '#8c8c8c' } }, 'Skipped')
      }
      return h(
        'span',
        { style: { color: row.ok ? '#52c41a' : '#ff4d4f', fontWeight: '500' } },
        row.ok ? 'OK' : 'Fail',
      )
    },
  },
  {
    title: 'Latency',
    key: 'latencyMs',
    width: 100,
    render: (row) => (row.latencyMs > 0 ? `${row.latencyMs} ms` : '—'),
  },
  {
    title: 'Detail',
    key: 'detail',
    ellipsis: { tooltip: true },
    render: (row) => row.detail ?? '—',
  },
]

// RefImages table state
const refImages = ref<any[]>([])
const loadingRefImages = ref(false)
const refImagesPage = ref(1)
const refImagesPageSize = ref(50)
const refImagesTotal = ref(0)
const refImagesSortBy = ref('ingestAt')
const refImagesSortOrder = ref<'asc' | 'desc'>('desc')
const refImagesSearch = ref('')

const refImagesSortOptions = [
  { label: 'ID', value: 'id' },
  { label: 'Ingest Date', value: 'ingestAt' },
  { label: 'Basename', value: 'basename' },
  { label: 'File Size', value: 'filesize' },
  { label: 'Width', value: 'width' },
  { label: 'Height', value: 'height' },
  { label: 'Format', value: 'format' },
  { label: 'Date Uploaded', value: 'dateUploaded' },
  { label: 'EXIF Date', value: 'exifDate' },
  { label: 'Subject', value: 'subject' },
  { label: 'Rating', value: 'rating' },
  { label: 'Date Created', value: 'dateCreated' },
  { label: 'Date Modified', value: 'dateModified' },
]

const refImagesPagination = computed(() => ({
  page: refImagesPage.value,
  pageSize: refImagesPageSize.value,
  pageCount: Math.ceil(refImagesTotal.value / refImagesPageSize.value),
  itemCount: refImagesTotal.value,
  showSizePicker: true,
  pageSizes: [25, 50, 100, 200],
  showQuickJumper: true,
}))

// RefImages table columns
const refImagesColumns: DataTableColumns<any> = [
  {
    title: 'ID',
    key: 'id',
    width: 120,
    render: (row) => h('span', {
      title: row.id || '',
    }, row.id ? row.id.substring(0, 10) : '-'),
  },
  {
    title: 'Basename',
    key: 'basename',
    width: 200,
    ellipsis: { tooltip: true },
  },
  {
    title: 'Dimensions',
    key: 'dimensions',
    width: 100,
    render: (row) => row.width && row.height ? `${row.width}x${row.height}` : '-',
  },
  {
    title: 'Subject',
    key: 'subject',
    width: 200,
    ellipsis: { tooltip: true },
  },
  {
    title: 'Keywords',
    key: 'keywords',
    width: 250,
    render: (row) => {
      if (!row.keywords) return '-'
      try {
        const keywords = typeof row.keywords === 'string' ? JSON.parse(row.keywords) : row.keywords
        if (Array.isArray(keywords) && keywords.length > 0) {
          return keywords.join(', ')
        }
        return '-'
      } catch (e) {
        return row.keywords
      }
    },
    ellipsis: { tooltip: true },
  },
  {
    title: 'Rating',
    key: 'rating',
    width: 80,
    render: (row) => row.rating ? `${row.rating}★` : '-',
  },
  {
    title: 'Ingest At',
    key: 'ingestAt',
    width: 180,
    render: (row) => new Date(row.ingestAt).toLocaleString(),
  },
  {
    title: 'Date Uploaded',
    key: 'dateUploaded',
    width: 180,
    render: (row) => row.dateUploaded ? new Date(row.dateUploaded).toLocaleString() : '-',
  },
  {
    title: 'User',
    key: 'user',
    width: 150,
    render: (row) => row.user?.email || row.userId || '-',
  },
]

// User table columns
const userColumns: DataTableColumns<User> = [
  {
    title: 'Email',
    key: 'email',
  },
  {
    title: 'Name',
    key: 'name',
    render: (row) => row.name || '-',
  },
  {
    title: 'Role',
    key: 'role',
    render: (row) => {
      const roleColors: Record<string, string> = {
        admin: '#ff4d4f',
        moderator: '#faad14',
        user: '#52c41a',
      }
      return h('span', {
        style: {
          color: roleColors[row.role] || '#666',
          fontWeight: '500',
        },
      }, row.role.toUpperCase())
    },
  },
  {
    title: 'Active',
    key: 'isActive',
    render: (row) => {
      return h('span', {
        style: {
          color: row.isActive ? '#52c41a' : '#ff4d4f',
        },
      }, row.isActive ? 'Yes' : 'No')
    },
  },
  {
    title: 'Created',
    key: 'createdAt',
    render: (row) => new Date(row.createdAt).toLocaleDateString(),
  },
]

// Brand table columns
const brandColumns: DataTableColumns<any> = [
  {
    title: 'Code',
    key: 'code',
    width: 120,
  },
  {
    title: 'Brand Code',
    key: 'brandCode',
    width: 120,
    render: (row) => row.brandCode || '-',
  },
  {
    title: 'Name',
    key: 'name',
    ellipsis: { tooltip: true },
  },
  {
    title: 'Description',
    key: 'description',
    width: 200,
    ellipsis: { tooltip: true },
    render: (row) => row.description || '-',
  },
  {
    title: 'Created',
    key: 'createdAt',
    width: 180,
    render: (row) => new Date(row.createdAt).toLocaleString(),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 100,
    render: (row) => {
      return h('div', { style: 'display: flex; gap: 8px;' }, [
        h('n-button', {
          size: 'small',
          type: 'error',
          onClick: () => handleDeleteBrand(row),
        }, {
          default: () => 'Delete'
        })
      ])
    },
  },
]

// Methods
const loadUsers = async () => {
  loadingUsers.value = true
  try {
    const response = await apiClient.listUsers()
    users.value = response.users
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to load users')
  } finally {
    loadingUsers.value = false
  }
}

const loadBrands = async () => {
  loadingBrands.value = true
  try {
    const response = await apiClient.getBrands({ limit: 1000 })
    brands.value = response.brands
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to load brands')
  } finally {
    loadingBrands.value = false
  }
}

const handleDeleteBrand = async (brand: any) => {
  brandToDelete.value = brand
  showDeleteDialog.value = true
}

const confirmDeleteBrand = async () => {
  if (!brandToDelete.value) return
  
  try {
    const result = await apiClient.deleteBrand(brandToDelete.value.id)
    const deleted = result.deleted || {
      comps: 0,
      images: 0,
      layouts: 0,
      projects: 0,
      assets: 0,
      deliverables: 0,
      versions: 0
    }
    message.success(`Brand "${brandToDelete.value.name}" deleted successfully. Removed: ${deleted.projects || 0} projects, ${deleted.comps || 0} comps, ${deleted.images || 0} images, ${deleted.assets || 0} assets, ${deleted.deliverables || 0} deliverables.`)
    showDeleteDialog.value = false
    brandToDelete.value = null
    await loadBrands()
    await loadDbStats() // Refresh stats
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to delete brand')
  }
}

const cancelDeleteBrand = () => {
  showDeleteDialog.value = false
  brandToDelete.value = null
}

const loadDbStats = async () => {
  loadingDbStats.value = true
  try {
    const response = await apiClient.getDbStats()
    dbStats.value = response.stats
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to load database statistics')
  } finally {
    loadingDbStats.value = false
  }
}

const runIntegrationHealth = async () => {
  loadingIntegrationHealth.value = true
  try {
    const res = await apiClient.getIntegrationsHealth()
    integrationChecks.value = res.checks
    integrationCheckedAt.value = new Date(res.checkedAt).toLocaleString()
    const failed = res.checks.filter((c) => !c.ok && !c.detail?.toLowerCase().includes('skipped'))
    if (failed.length) {
      message.warning(`${failed.length} integration(s) failed — see table`)
    } else {
      message.success('All configured integrations responded')
    }
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to run integration checks')
  } finally {
    loadingIntegrationHealth.value = false
  }
}

const handleCreateUser = async () => {
  if (!createUserFormRef.value) return

  try {
    await createUserFormRef.value.validate()
    
    const { email, name, role } = createUserForm.value
    await apiClient.createUser(email, name || undefined, role)
    
    message.success('User created successfully with default password: inc-agency-2026')
    showCreateUserModal.value = false
    
    // Reset form
    createUserForm.value = {
      email: '',
      name: '',
      role: 'user',
    }
    
    // Reload users
    await loadUsers()
  } catch (error: any) {
    if (error.response?.data?.error) {
      message.error(error.response.data.error)
    } else if (error.message) {
      message.error(error.message)
    }
  }
}

// RefImages methods
const loadRefImages = async () => {
  loadingRefImages.value = true
  try {
    const response = await apiClient.getRefImages({
      page: refImagesPage.value,
      limit: refImagesPageSize.value,
      sortBy: refImagesSortBy.value,
      sortOrder: refImagesSortOrder.value,
      search: refImagesSearch.value || undefined,
    })
    refImages.value = response.refImages
    refImagesTotal.value = response.pagination.total
  } catch (error: any) {
    message.error(error.response?.data?.error || 'Failed to load refImages')
  } finally {
    loadingRefImages.value = false
  }
}

const handleRefImagesPageChange = (page: number) => {
  refImagesPage.value = page
  loadRefImages()
}

const handleRefImagesPageSizeChange = (pageSize: number) => {
  refImagesPageSize.value = pageSize
  refImagesPage.value = 1
  loadRefImages()
}

const handleRefImagesSearch = () => {
  refImagesPage.value = 1
  loadRefImages()
}

// Lifecycle
onMounted(() => {
  loadUsers()
  loadBrands()
  loadDbStats()
  loadRefImages()
})
</script>

<style scoped>
.admin-tab {
  height: 100%;
  overflow-y: auto;
}

.api-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--n-text-color-3);
  line-height: 1.5;
}
</style>

